import { useEffect, useState } from 'react'
import { Check, ChevronDown, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Alert, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/shared/lib/utils'
import {
  PRODUCT_LEVEL_REQUIREMENTS_TOTAL,
  useProductLevelRequirementsProgress,
} from '../hooks/useProductLevelRequirementsProgress'
import {
  readV2ProductGetStartedDismissed,
  writeV2ProductGetStartedDismissed,
} from '../utils/v2ProductGetStartedDismissal'

type V2ProductGetStartedCardProps = {
  productId: string
}

type GetStartedProgressBarProps = {
  completedCount: number
  total: number
  isComplete: boolean
  label: string
}

function GetStartedProgressBar({
  completedCount,
  total,
  isComplete,
  label,
}: GetStartedProgressBarProps) {
  const progressPercent =
    total === 0 ? 0 : Math.round((completedCount / total) * 100)

  return (
    <div className='space-y-2'>
      <div className='flex items-center justify-between gap-2 text-xs'>
        <span
          className={
            isComplete ? 'text-emerald-800 dark:text-emerald-200' : undefined
          }
        >
          {label}
        </span>
      </div>
      <div
        className='h-2.5 w-full overflow-hidden rounded-full bg-muted'
        role='progressbar'
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={completedCount}
        aria-label={label}
      >
        <div
          className={cn(
            'h-full rounded-full transition-[width]',
            isComplete ? 'bg-emerald-600 dark:bg-emerald-400' : 'bg-primary'
          )}
          style={{ width: `${progressPercent}%` }}
        />
      </div>
    </div>
  )
}

type GetStartedStepChecklistItemProps = {
  title: string
  hint: string
  complete: boolean
}

function GetStartedStepChecklistItem({
  title,
  hint,
  complete,
}: GetStartedStepChecklistItemProps) {
  return (
    <li className='space-y-1'>
      <div className='flex items-center gap-2'>
        {complete ? (
          <span
            className='flex size-4 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300'
            aria-hidden
          >
            <Check className='size-2.5' />
          </span>
        ) : (
          <span
            className='size-4 shrink-0 rounded-full border border-muted-foreground/40'
            aria-hidden
          />
        )}
        <span className='text-sm font-medium'>{title}</span>
      </div>
      <p className='ps-6 text-xs leading-relaxed text-muted-foreground'>
        {hint}
      </p>
    </li>
  )
}

export function V2ProductGetStartedCard({
  productId,
}: V2ProductGetStartedCardProps) {
  const { t } = useTranslation('common')
  const {
    isLoading,
    hasProductDescription,
    hasProductIdentification,
    hasManufacturerInformation,
    completedCount,
    isComplete,
  } = useProductLevelRequirementsProgress(productId)
  const [openOverride, setOpenOverride] = useState<boolean | null>(null)
  const [dismissed, setDismissed] = useState(() =>
    readV2ProductGetStartedDismissed(productId)
  )

  useEffect(() => {
    setOpenOverride(null)
    setDismissed(readV2ProductGetStartedDismissed(productId))
  }, [productId])

  useEffect(() => {
    if (isComplete) {
      setOpenOverride(false)
    }
  }, [isComplete])

  let open = false
  if (!isLoading) {
    open = openOverride ?? !isComplete
  }

  if (dismissed) {
    return null
  }

  if (isLoading) {
    return (
      <Alert variant='info' className='items-center [&>svg]:translate-y-0' aria-busy='true'>
        <div className='col-start-2 flex w-full items-center gap-2'>
          <Skeleton className='h-4 min-h-4 flex-1 max-w-md' />
          <Skeleton className='size-4 shrink-0 rounded-sm' />
        </div>
        <div className='col-span-2 col-start-1 my-2 w-full'>
          <Skeleton className='mb-2 h-3 w-24' />
          <Skeleton className='h-2.5 w-full' />
        </div>
      </Alert>
    )
  }

  const progressLabel = t('v2.pages.products.detail.getStarted.progressLabel', {
    completed: completedCount,
    total: PRODUCT_LEVEL_REQUIREMENTS_TOTAL,
  })

  let alertTitle = t('v2.pages.products.detail.getStarted.title')
  let alertDescription = t('v2.pages.products.detail.getStarted.description')
  if (isComplete) {
    alertTitle = t('v2.pages.products.detail.getStarted.completeTitle')
    alertDescription = t('v2.pages.products.detail.getStarted.completeDescription')
  }

  return (
    <Alert
      variant={isComplete ? 'success' : 'info'}
      className='items-center [&>svg]:translate-y-0'
    >
      <Collapsible
        open={open}
        onOpenChange={setOpenOverride}
        className='contents'
      >
        <div className='col-start-2 flex w-full items-center gap-1'>
          <CollapsibleTrigger className='flex min-w-0 flex-1 items-center gap-2 text-start'>
            <AlertTitle className='col-start-auto line-clamp-none min-h-4 flex-1'>
              {alertTitle}
            </AlertTitle>
            <ChevronDown
              className='size-4 shrink-0 text-muted-foreground transition-transform duration-200 in-data-panel-open:rotate-180'
              aria-hidden
            />
          </CollapsibleTrigger>
          {isComplete ? (
            <Button
              type='button'
              variant='ghost'
              size='icon-sm'
              className='shrink-0 text-muted-foreground hover:text-foreground'
              aria-label={t(
                'v2.pages.products.detail.getStarted.dismissAriaLabel'
              )}
              onClick={() => {
                writeV2ProductGetStartedDismissed(productId)
                setDismissed(true)
              }}
            >
              <X className='size-4' aria-hidden />
            </Button>
          ) : null}
        </div>
        <div className='col-span-2 col-start-1 my-2 w-full'>
          <GetStartedProgressBar
            completedCount={completedCount}
            total={PRODUCT_LEVEL_REQUIREMENTS_TOTAL}
            isComplete={isComplete}
            label={progressLabel}
          />
        </div>
        <CollapsibleContent className='col-span-2 col-start-1 space-y-4'>
          <p className='text-sm leading-relaxed text-muted-foreground'>
            {alertDescription}
          </p>
          <ul className='space-y-4'>
            <GetStartedStepChecklistItem
              title={t(
                'v2.pages.products.detail.rohsBriefing.steps.items.productDescription.title'
              )}
              hint={t(
                'v2.pages.products.detail.getStarted.steps.productDescription.hint'
              )}
              complete={hasProductDescription}
            />
            <GetStartedStepChecklistItem
              title={t(
                'v2.pages.products.detail.rohsBriefing.steps.items.productIdentification.title'
              )}
              hint={t(
                'v2.pages.products.detail.getStarted.steps.productIdentification.hint'
              )}
              complete={hasProductIdentification}
            />
            <GetStartedStepChecklistItem
              title={t(
                'v2.pages.products.detail.rohsBriefing.steps.items.manufacturerInformation.title'
              )}
              hint={t(
                'v2.pages.products.detail.getStarted.steps.manufacturerInformation.hint'
              )}
              complete={hasManufacturerInformation}
            />
          </ul>
        </CollapsibleContent>
      </Collapsible>
    </Alert>
  )
}
